import { FileType } from 'rsuite/Uploader';
import { useMutation } from '@apollo/client';
import {
	AdAssetType,
	AssetSubtype,
	CreateSpotifyAssetsDocument,
	MediaInput,
	MediaType,
	SpotifyMediaListDocument,
	UploadSpotifyMediaDocument,
	UploadSpotifyMediaMutation,
} from 'wb-spotify/graphql/generated';
import {
	alephToast,
	Button,
	ButtonAppearance,
	ButtonColor,
	ButtonSize,
	cn,
	NotificationToast,
	NotificationType,
	Tooltip,
	TrashCanIcon,
	UploadCellComponent,
	WarningSign,
	WaveformIcon,
	NoValuePlaceholder,
} from '@aleph/shared-components';
import { ICellRendererParams } from 'ag-grid-community';
import { useEffect, useMemo } from 'react';
import { canEditMedia } from 'wb-spotify/app/routes/superGrid/columnDefinitions/ad';
import { fallBackCover } from 'wb-spotify/app/routes/superGrid/columnEditors/media/column';
import { Trans, useTranslation } from 'react-i18next';
import MediaInfoBubble from 'wb-spotify/app/routes/superGrid/cellRenderers/mediaInfoBubble';
import { useSuperGridValidate } from '@aleph/supergrid';
import { getGraphqlFormErrors } from 'wb-spotify/app/common/utils';

export const supportedImageFormats = ['jpeg', 'png'];
export const supportedVideoFormats = ['mp4', 'mov'];
export const supportedAudioFormats = ['mp3', 'ogg', 'wav'];

const AUDIO_MAX_SIZE_MB = 50;
const VIDEO_MAX_SIZE_MB = 500;
const IMAGE_MAX_SIZE_MB = 3;

function isUploadMediaDataValid(uploadMediaData?: UploadSpotifyMediaMutation | null): boolean {
	return (
		uploadMediaData?.uploadMedia !== null
		&& uploadMediaData?.uploadMedia !== undefined
		&& uploadMediaData?.uploadMedia?.mediaId !== null
		&& uploadMediaData?.uploadMedia?.mediaUrl !== null
		&& uploadMediaData?.uploadMedia?.mediaType !== null
		&& uploadMediaData?.uploadMedia?.mediaOriginalFileName !== null
		&& uploadMediaData?.uploadMedia?.mediaOriginalFileName !== undefined
	);
}

export function getFileNameExtension(fileName: string): string {
	const nameSegments = fileName.split('.') ?? [];

	if (nameSegments.length < 2) {
		return '';
	}

	return nameSegments.at(-1)?.toLowerCase() ?? '';
}

const transformColName = (colId: string): 'asset' | 'companion' | 'logo' | 'canvas' => {
	switch (colId) {
		case 'adCompanionAsset':
			return 'companion';
		case 'adLogoAsset':
			return 'logo';
		case 'adCanvasAsset':
			return 'canvas';
		case 'adAsset':
		default:
			return 'asset';
	}
};

const getAssetType = (colId: string): AdAssetType => {
	switch (colId) {
		case 'adCompanionAsset':
			return AdAssetType.CompanionAsset;
		case 'adLogoAsset':
			return AdAssetType.LogoAsset;
		case 'adCanvasAsset':
			return AdAssetType.CanvasAsset;
		case 'adAsset':
		default:
			return AdAssetType.Asset;
	}
};

type MediaColId = 'adCompanionAsset' | 'adLogoAsset' | 'adCanvasAsset' | 'adAsset';

const SpotifyMediaCellRenderer = (params: ICellRendererParams & { disabled?: boolean; mediaTypes: MediaType[] }) => {
	const [uploadMedia, { loading }] = useMutation(UploadSpotifyMediaDocument, {
		errorPolicy:         'all',
		refetchQueries:      [SpotifyMediaListDocument],
		awaitRefetchQueries: true,
	});

	const [createSpotifyAssets, { loading: createAssetsLoading }] = useMutation(CreateSpotifyAssetsDocument, {
		errorPolicy: 'all',
	});

	const { t } = useTranslation();

	const field = useMemo(
		// @ts-ignore: field is on params, probably added by supergrid
		() => (params?.field ?? params.colDef?.field ?? params.colDef?.colId ?? 'adAsset') as MediaColId,
		[params.colDef?.field, params.colDef?.colId],
	);

	const disabled = useMemo(() => {
		if (params.disabled) return true;

		return !canEditMedia(params, field);
	}, [params, field]);

	useEffect(() => {
		// if user changed format we need to reset value, only in creation mode
		if (params.data?.isInCreation && disabled && params.data[field]) {
			params?.setValue?.(null);
		}

		// if user cleared the value with backslash we need to reset it to null
		if (params?.value === '') {
			params?.setValue?.(null);
		}
	}, [disabled]);

	const { context: { validations } } = useSuperGridValidate();
	const assetValidationErrs = useMemo(() => {
		const adAssetsValidation = validations.find(val => val.field === 'adAssets');
		if (!adAssetsValidation) return null;

		const transformedColName = transformColName(field);

		if (adAssetsValidation.message?.toLowerCase?.()?.includes?.(transformedColName)) {
			return adAssetsValidation;
		}

		return null;
	}, [validations, field]);

	const clearMedia = () => {
		params?.setValue?.(null);
	};

	const accept = useMemo(() => params.mediaTypes.map((type => {
		switch (type) {
			case MediaType.Image:
				return supportedImageFormats.map(format => `image/${format}`).join(', ');
			case MediaType.Video:
				return supportedVideoFormats.map(format => `video/${format}`).join(', ');
			case MediaType.Audio:
				return supportedAudioFormats.map(format => `audio/${format}`).join(', ');
			default:
				return '';
		}
	})).join(), [params.mediaTypes]);

	const onUpload = async (files: FileType[]) => {
		const fileExtension = getFileNameExtension(files[0].name || '');

		let media: MediaInput;
		let maxFileSizeBytes = 1024 * 1024;

		switch (fileExtension) {
			case 'mp3':
			case 'ogg':
			case 'wav':
				media = {
					audio: files[0].blobFile,
				};

				maxFileSizeBytes *= AUDIO_MAX_SIZE_MB;

				break;

			case 'mp4':
			case 'mov':
				media = {
					video: files[0].blobFile,
				};

				maxFileSizeBytes *= VIDEO_MAX_SIZE_MB;

				break;

			case 'png':
			case 'jpeg':
			default:
				media = {
					image: files[0].blobFile,
				};

				maxFileSizeBytes *= IMAGE_MAX_SIZE_MB;

				break;
		}

		if ((files?.[0]?.blobFile?.size ?? 0) > maxFileSizeBytes) {
			const maxSize = maxFileSizeBytes / 1024 / 1024;

			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={t('sp: fileSizeTooBig', 'File Too Large')}
					description={<Trans
						i18nKey="sp: fileSizeTooBigMessage"
						defaults="The file you are trying to upload exceeds the {{maxSize}}MB limit."
						values={{
							maxSize,
						}}
					>
						The file you are trying to upload exceeds the {maxSize}MB limit.
					</Trans>}
				/>,
			);

			return;
		}

		try {
			const { data: uploadMediaData, errors } = await uploadMedia({
				variables: {
					media,
					adAccountId: params?.data?.parent?.parent?.parent?.adAccountId ?? '',
				},
			});

			if (!isUploadMediaDataValid(uploadMediaData)) {
				throw new Error('Failed to upload media');
			}

			if (errors?.length) {
				throw new Error(
					getGraphqlFormErrors([...errors])
						?? t('sp: media_upload_general_err', 'Something went wrong while uploading media'),
				);
			}

			const uploadMediaResult = uploadMediaData!.uploadMedia;

			// If uploaded media is audio, always use USER_UPLOADED_AUDIO subtype
			const assetType = uploadMediaResult.mediaType;
			const adAssetType = getAssetType(field);
			const assetSubtype = assetType === MediaType.Audio && adAssetType === AdAssetType.Asset
				? AssetSubtype.UserUploadedAudio
				: undefined;

			// Create the asset
			try {
				const { data: createAssetsData, errors: createAssetsErrors } = await createSpotifyAssets({
					variables: {
						assets: [
							{
								assetName:   uploadMediaResult.mediaOriginalFileName ?? '',
								mediaUrl:    uploadMediaResult.mediaUrl ?? '',
								assetType:   uploadMediaResult.mediaType as MediaType,
								assetHash:   `${transformColName(field)}_${uploadMediaResult.mediaId}`,
								mediaId:     uploadMediaResult.mediaId,
								adAccountId: params?.data?.parent?.parent?.parent?.adAccountId ?? '',
								adAssetType: getAssetType(field),
								...(assetSubtype ? { assetSubtype } : {}),
							},
						],
					},
				});

				// We can assume only one is returned
				const asset = createAssetsData?.createAssets[0];

				// If no asset id throw error
				if (!asset?.assetId) {
					throw new Error(
						getGraphqlFormErrors(createAssetsErrors ? [...createAssetsErrors] : [])
							?? t('sp: media_upload_general_err', 'Something went wrong while uploading media'),
					);
				}

				params?.setValue?.({
					...asset,
					name:           asset?.assetName ?? uploadMediaResult.mediaOriginalFileName ?? '',
					mediaThumbnail: asset?.assetUrl ?? uploadMediaResult.mediaUrl ?? '',
					mediaUrl:       asset?.assetUrl ?? uploadMediaResult.mediaUrl ?? '',
					id:             asset.assetId,
					mediaType:      (asset?.assetType ?? uploadMediaResult.mediaType ?? MediaType.Image) as MediaType,
					hash:           `${transformColName(field)}_${asset?.assetHash}`,
				});
			} catch (createError: any) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={t('sp: media_upload_general_err_title', 'Upload failed')}
						description={
							createError?.message
							?? t('sp: media_upload_general_err_description', 'Something went wrong while uploading media')
						}
					/>,
				);
			}
		} catch (e: any) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={t('sp: media_upload_general_err_title', 'Upload failed')}
					description={
						e?.message
						?? t('sp: media_upload_general_err_description', 'Something went wrong while uploading media')
					}
				/>,
			);
		}
	};

	const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
		const img = e.currentTarget;
		img.onerror = null;
		img.src = fallBackCover(params?.value?.mediaType as MediaType);
	};

	if (!params.value && params.data.isCreate) {
		return <NoValuePlaceholder />;
	}

	if (!params.value) {
		return (
			<div className="w-full flex items-center justify-between">
				<UploadCellComponent
					loading={loading || createAssetsLoading}
					mediaUrl={params.data.mediaThumbnail}
					mediaName={params.data.mediaName}
					onUpload={onUpload}
					accept={accept}
					disabled={disabled}
					hideFileList
					className="w-full"
				/>

				{!disabled && (
					<MediaInfoBubble
						className="mr-[8px]"
						field={field}
						groupFormat={params?.data?.parent?.groupAssetFormat}
					/>
				)}
			</div>
		);
	}

	return (
		<div className={cn('flex gap-[8px] pr-[8px] items-center h-full overflow-hidden overflow-ellipsis', {
			'border border-red-500 rounded p-[4px]': assetValidationErrs,
		})}
		>
			{params.value.type === 'AUDIO'
				? (
					<div className="h-full aspect-square flex items-center justify-center">
						<WaveformIcon className="w-[16px] h-[16px]" />
					</div>
				)
				: (
					<img
						alt="media"
						className="h-full aspect-square object-cover"
						src={params.value.mediaThumbnail ?? params.value.mediaUrl}
						onError={handleImageError}
					/>
				)
			}

			<div className='flex-1 overflow-hidden overflow-ellipsis'>
				{params.value.name ?? params.value.mediaUrl}
			</div>

			{assetValidationErrs && (
				<Tooltip
					backgroundColor="bg-red-500"
					showArrow
					size="large"
					placement="top-end"
					offset={16}
					content={assetValidationErrs.message}
				>
					<WarningSign
						width={16}
						height={16}
						className="text-red-500"
						fill="hsl(var(--red-500))"
					/>
				</Tooltip>
			)}

			<Button
				onClick={clearMedia}
				leftNode={<TrashCanIcon />}
				color={ButtonColor.RED}
				size={ButtonSize.SMALL}
				appearance={ButtonAppearance.OUTLINED}
			/>
		</div>
	);
};

export default SpotifyMediaCellRenderer;
